<template>
  <div class="account-creation-dialog">
    <v-dialog
      v-model="dialogVisible"
      persistent
      :content-class="'create_new_account_modal account_creation_dialog'"
      max-width="550"
    >
      <!-- Step 1: Account Creation Form -->
      <div v-if="!showPaymentStep && !showSuccessMessage">
        <div class="heading">
          <h2 class="heading_new_account" v-text="`Create ${accountType} Account`"></h2>
        </div>
        
        <div class="wrapper_content">
          <div class="create_account_box">
            <div class="set_name_account">
              <div class="label_name">Choose a unique name</div>
              <input 
                type="text" 
                v-model.trim="accountName"
                :class="{'error_name': createAccountError || !uniqueAccountName && accountName.length > 0}"
                :placeholder="`Enter new ${accountType.toLowerCase()} name`"
                @input="checkAccountName"
              >
              <span class="err_artist_exists" v-if="!uniqueAccountName && accountName.length > 0">
                <span v-text="`${accountType} exists. Choose another ${accountType} Name.`"></span>
              </span>
              <span class="err_artist_exists" v-if="$v.accountName.$dirty && !$v.accountName.maxLength">
                30 characters max
              </span>
              <span class="err_artist_exists" v-if="$v.accountName.$dirty && !$v.accountName.required">
                Account Name is required
              </span>
            </div>
            
            <div class="set_photo" style="margin-bottom: 30px;">
              <div class="label_photo" v-text="`Profile ${isArtist ? 'photo' : 'logo'}`"></div>
              <div class="image-wrapper" style="display: flex; align-items: center;">
                <input
                  type="file"
                  hidden
                  ref="fileInput"
                  accept="image/png, image/jpeg"
                  @change="photoSelected"
                >
                
                <!-- Cover-style image selector -->
                <div 
                  class="select_cover_album" 
                  :class="{'default_img': !selectedPhotoPreviewUrl}" 
                  @click="selectPhoto"
                  @mouseover="photoHoverState = true"
                  @mouseleave="photoHoverState = false"
                  :style="{
                    width: '150px',
                    height: '150px',
                    display: 'flex',
                    cursor: 'pointer',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    border: `2px dashed ${photoHoverState ? '#20C4F5' : '#5F6986'}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    backgroundColor: '#182034',
                    marginRight: '20px'
                  }"
                >
                  <!-- Show selected image if available -->
                  <img 
                    class="cover_select_img" 
                    v-if="selectedPhotoPreviewUrl" 
                    :src="selectedPhotoPreviewUrl" 
                    :alt="`${accountType} profile ${isArtist ? 'photo' : 'logo'}`"
                    style="width: 100%; height: 100%; object-fit: cover; z-index: 2;"
                  >
                  
                  <!-- Placeholder when no image is selected -->
                  <div 
                    v-if="!selectedPhotoPreviewUrl" 
                    class="placeholder-image"
                    style="
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: calc(100% - 36px);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      z-index: 1;
                    "
                  >
                    <svg width="60" height="60" viewBox="0 0 157 97" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M96 28C103.732 28 110 21.732 110 14C110 6.26801 103.732 0 96 0C88.268 0 82 6.26801 82 14C82 21.732 88.268 28 96 28ZM9 97C4.02944 97 0 92.9706 0 88V83.3199C0 81.4135 0.605399 79.5563 1.72892 78.016L34.4807 33.1169C37.8861 28.4484 44.7461 28.1531 48.5402 32.5117L76.8689 65.0557C80.1267 68.7984 85.7992 69.1981 89.5498 65.9494L113.34 45.3421C116.786 42.3576 121.919 42.4205 125.29 45.4885L154.057 71.667C155.932 73.3726 157 75.7893 157 78.3234V88C157 92.9706 152.971 97 148 97H9Z" fill="#2E3548"/>
                    </svg>
                  </div>
                  <span 
                    class="select_img"
                    :style="{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '36px',
                      minHeight: '36px',
                      backgroundColor: selectedPhotoPreviewUrl ? 'rgba(95, 105, 134, 0.7)' : '#5F6986',
                      color: '#fff',
                      letterSpacing: '0.03em',
                      fontWeight: '500',
                      fontSize: '16px',
                      zIndex: 3,
                      transition: '0.25s',
                      position: 'absolute',
                      bottom: 0,
                      width: '100%'
                    }"
                  >{{ selectedPhotoPreviewUrl ? 'Change' : 'Select' }}</span>
                </div>
                
                <div class="description_photo" style="display: flex; flex-direction: column; justify-content: center; color: #7481A6;">
                  <div><span style="font-weight: 700; padding-right: 5px;">Size:</span> 10 MB or smaller</div>
                  <div><span style="font-weight: 700; padding-right: 5px;">Format:</span> JPG, PNG</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" @click="cancel">Cancel</button>
          <button 
            type="button" 
            class="tetriatary_btn" 
            @click="createNewAccount"
            :class="{loading: isLoading}"
            :disabled="!canCreateAccount"
          >
            <v-progress-circular v-if="isLoading" :width="2" indeterminate></v-progress-circular>
            Next
          </button>
        </div>
      </div>
      
      <!-- Step 2: Payment Form -->
      <div v-else-if="showPaymentStep">
        <div class="heading">
          <h2 class="heading_new_account">{{ accountType }} Subscription</h2>
        </div>
        
        <div class="wrapper_content payment-form-wrapper">
          <div class="info_card_box">
            <div class="account_data" v-if="!isLoading">
              <div class="description_info">
                <div class="image" :class="{default_ava: !selectedPhotoPreviewUrl}">
                  <img
                    v-if="selectedPhotoPreviewUrl"
                    :src="selectedPhotoPreviewUrl"
                    :alt="`${accountType} profile ${isArtist ? 'photo' : 'logo'}`"
                    style="width: 100%; height: 100%; object-fit: cover;"
                  >
                  <div v-if="!selectedPhotoPreviewUrl" class="default_user_avatar"></div>
                </div>
                <div class="name_type">
                  <div class="name">{{ accountName }}</div>
                  <div class="type">{{ accountType }}</div>
                </div>
              </div>
              <div class="price_info">
                $9.99/mo
              </div>
            </div>
            
            <div class="card_data">
              <StripeElementPayment
                v-if="clientSecret"
                ref="paymentRef"
                :pk="stripeToken"
                :elements-options="stripeOptions"
                :confirm-params="confirmParams"
                :redirect="'if_required'"
                :load-complete="handleElementReady"
                @error="submitPaymentCallback"
                @success="submitPaymentCallback"
              ></StripeElementPayment>
              <div v-if="!clientSecret" class="loading-indicator">
                <span>Loading payment form...</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" @click="backToAccountCreation">Back</button>
          <button 
            type="button" 
            class="tetriatary_btn pay" 
            @click="submitPayment"
            :class="{loading: submitStripeLoader}"
          >
            <v-progress-circular v-if="submitStripeLoader" :width="2" indeterminate></v-progress-circular>
            Pay Now
          </button>
        </div>
      </div>
      
      <!-- Step 3: Success message after account creation -->
      <div v-else-if="showSuccessMessage">
        <div class="heading">
          <h2 class="heading_new_account">Success!</h2>
        </div>
        
        <div class="wrapper_content">
          <div class="success_message">
            <p>Your {{ accountType.toLowerCase() }} account <strong>{{ createdAccountName }}</strong> has been created successfully!</p>
            <p v-if="paymentComplete">Your subscription has been activated.</p>
          </div>
        </div>
        
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" @click="done">Done</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { StripeElementPayment } from '@vue-stripe/vue-stripe';

export default {
  name: 'AccountCreationDialog',
  components: {
    StripeElementPayment
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    accountType: {
      type: String,
      default: 'Artist',
      validator: (value) => ['Artist', 'Label'].includes(value)
    }
  },
  data() {
    return {
      accountName: '',
      selectedFile: null,
      selectedPhotoPreviewUrl: null,
      uniqueAccountName: true,
      isLoading: false,
      showSuccessMessage: false,
      showPaymentStep: false,
      createdAccountName: '',
      createAccountError: false,
      checkingName: false,
      nameCheckTimer: null,
      activeSubscriptions: [],  // Track active subscriptions for cleanup
      photoHoverState: false,  // Track hover state for photo selector
      
      // Centrifuge related data
      centrifugeUrl: process.env.VUE_APP_CENTRIFUGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL,
      centrifugeCallback: null,
      centrifugeSelf: null,
      
      // Stripe payment related data
      stripeToken: process.env.VUE_APP_STRIPE_API_TOKEN,
      clientSecret: '',
      stripeOptions: {
        clientSecret: '',
        appearance: {
          variables: {
            fontSizeBase: '16px',
            colorText: '#ffffff',
            colorDanger: '#E94E4E',
            borderRadius: '5px',
            colorBackground: '#0C1324',
            colorIconTabSelected: '#ffffff',
          },
          rules: {
            '.Tab': {
              padding: '12px 12px 7px 12px',
              border: 'none',
              backgroundColor: '#182034',
              boxShadow: 'none'
            },
            '.Tab--selected, .Tab--selected:focus': {
              border: '1px solid #5F6986',
              boxShadow: 'none',
            },
            '.TabLabel': {
              color: '#ffffff',
            },
            '.Input': {
              boxShadow: 'none',
            },
            '.Input--invalid': {
              boxShadow: 'none',
              border: '1px solid #E94E4E'
            },
            '.Input:focus': {
              boxShadow: 'none',
              border: '1px solid #5F6986'
            },
            '.Input::placeholder': {
              color: '#7481A6',
            },
            '.Error': {
              fontSize: '12px',
              marginLeft: '5px'
            }
          }
        }
      },
      confirmParams: {
        return_url: window.location.href
      },
      submitStripeLoader: false,
      paymentComplete: false,
      paymentError: null
    };
  },
  validations: {
    accountName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(30)
    }
  },
  // Lifecycle hooks
  beforeDestroy() {
    // Clean up all connections and event listeners when component is destroyed
    console.log('Component being destroyed, cleaning up connections');
    this.cleanupConnections();
    
    // Remove any remaining event listeners
    window.removeEventListener("message", this.handleStripeMessage);
    
    // Clear any pending timers
    if (this.nameCheckTimer) {
      clearTimeout(this.nameCheckTimer);
    }
  },
  
  computed: {
    ...mapGetters({
      userID: 'userID',
      subscriptionClientSecret: 'SUBSCRIPTION_CLIENT_SECRET',
      recordLabelClientSecret: 'RECORD_LABEL_CLIENT_SECRET',
      YOUR_ARTISTS: 'YOUR_ARTISTS',
      RECORD_LABELS: 'RECORD_LABELS',
      uniqueArtistName: 'UNIQUE_ARTIST_NAME',
      uniqueLabelName: 'UNIQUE_LABEL_NAME',
      centrifugeClient: 'CENTRIFUGE_CLIENT',
      FILE_URL: 'FILE_URL'
    }),
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isArtist() {
      return this.accountType === 'Artist';
    },
    canCreateAccount() {
      return this.accountName.length > 0 && 
             this.accountName.length <= 30 && 
             this.uniqueAccountName &&
             !this.isLoading &&
             !this.checkingName &&
             !this.$v.accountName.$error;
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // Reset form when dialog is opened
        this.resetForm();
      } else {
        // Dialog is being closed, clean up any active connections
        this.cleanupConnections();
      }
    },
    showPaymentStep(newVal) {
      // If payment step is closed/hidden, clean up connections
      if (!newVal && this.activeSubscriptions.length > 0) {
        this.cleanupConnections();
      }
    },
    accountName(newVal) {
      // Clear any existing timer
      if (this.nameCheckTimer) {
        clearTimeout(this.nameCheckTimer);
      }
      
      // Set checkingName to true immediately to show loading state
      if (newVal && newVal.length > 0) {
        this.checkingName = true;
        // Default to not unique until we verify
        this.uniqueAccountName = false;
      } else {
        this.checkingName = false;
        this.uniqueAccountName = true;
        return;
      }
      
      // Debounce the actual check by 300ms
      this.nameCheckTimer = setTimeout(() => {
        this.checkAccountName();
      }, 300);
    }
  },
  methods: {
    ...mapActions([
      'CREATE_ARTIST',
      'CREATE_RECORD_LABEL',
      'GET_YOUR_ARTISTS',
      'GET_RECORD_LABELS',
      'CHECK_UNIQUE_ARTIST_NAME',
      'CHECK_UNIQUE_LABEL_NAME',
      'GET_CENTRIFUGE_CLIENT',
      'ADD_FILE',
      'CHANGE_ARTIST_DATA'
    ]),
    resetForm() {
      // Clean up any existing connections first
      this.cleanupConnections();
      
      this.accountName = '';
      this.selectedFile = null;
      this.selectedPhotoPreviewUrl = null;
      this.uniqueAccountName = true;
      this.isLoading = false;
      this.showSuccessMessage = false;
      this.createdAccountName = '';
      this.createAccountError = false;
      this.checkingName = false;
    },
    
    /**
     * Clean up all active Centrifuge connections and subscriptions
     */
    cleanupConnections() {
      // Unsubscribe from all active subscriptions
      if (this.activeSubscriptions.length > 0) {
        console.log('Cleaning up', this.activeSubscriptions.length, 'active subscriptions');
        this.activeSubscriptions.forEach(sub => {
          if (sub && typeof sub.unsubscribe === 'function') {
            sub.unsubscribe();
          }
        });
        this.activeSubscriptions = [];
      }
      
      // Disconnect the Centrifuge client if it exists
      if (this.centrifugeClient && typeof this.centrifugeClient.disconnect === 'function') {
        console.log('Disconnecting Centrifuge client');
        this.centrifugeClient.disconnect();
      }
    },
    resetImageUploader() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    selectPhoto() {
      this.$refs.fileInput.click();
    },
    photoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (10MB limit)
      const limitImageSize = 10 * 1024 * 1024;
      if (file.size > limitImageSize) {
        // Show error message
        alert('File size exceeds the 10MB limit');
        return;
      }
      
      // Check file type
      if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
        // Show error message
        alert('Only JPEG and PNG files are allowed');
        return;
      }
      
      this.selectedFile = file;
      this.selectedPhotoPreviewUrl = URL.createObjectURL(file);
    },
    checkAccountName() {
      if (!this.accountName || this.accountName.length === 0) {
        this.uniqueAccountName = true;
        this.checkingName = false;
        return;
      }
      
      try {
        if (this.isArtist) {
          // First, check against existing artists in the store for an immediate response
          if (this.YOUR_ARTISTS) {
            const artists = Array.isArray(this.YOUR_ARTISTS) ? this.YOUR_ARTISTS : 
                          (this.YOUR_ARTISTS['hydra:member'] ? this.YOUR_ARTISTS['hydra:member'] : []);
            
            const nameExists = artists.some(artist => 
              artist.name && artist.name.toLowerCase() === this.accountName.toLowerCase()
            );
            
            if (nameExists) {
              this.uniqueAccountName = false;
              this.checkingName = false;
              return; // Exit early if we found a duplicate
            }
          }
          
          // Then check with the API
          let formData = new FormData();
          formData.append('name', this.accountName);
          
          // Call the Vuex action and handle errors at component level
          this.CHECK_UNIQUE_ARTIST_NAME(formData)
            .then(() => {
              // On success, the UNIQUE_ARTIST_NAME getter will be updated
              console.log('Artist name check API call successful');
              this.uniqueAccountName = this.uniqueArtistName;
            })
            .catch(err => {
              console.log(`checkUniqueArtistName error: ${err}`);
              
              // Check if this was an aborted request
              if (axios.isCancel(err)) {
                console.log('Request was canceled due to new input');
                // Don't update uniqueAccountName - we'll wait for the latest request
                return;
              }
              
              // For other API errors, rely on our local check
              // We only get here if the local check didn't find a match
              // So we can say it's unique locally
              this.uniqueAccountName = true;
            })
            .finally(() => {
              this.checkingName = false;
            });
        } else {
          // Check for unique label name
          // First check against existing labels in the store
          if (this.RECORD_LABELS) {
            const labels = Array.isArray(this.RECORD_LABELS) ? this.RECORD_LABELS : 
                          (this.RECORD_LABELS['hydra:member'] ? this.RECORD_LABELS['hydra:member'] : []);
            
            const titleExists = labels.some(label => 
              label.title && label.title.toLowerCase() === this.accountName.toLowerCase()
            );
            
            if (titleExists) {
              this.uniqueAccountName = false;
              this.checkingName = false;
              return; // Exit early if we found a duplicate
            }
          }
          
          // Then check with the API
          let formData = new FormData();
          formData.append('title', this.accountName);
          
          // Call the Vuex action and handle any errors
          this.CHECK_UNIQUE_LABEL_NAME(formData)
            .catch(err => console.log(`checkUniqueLabelName error: ${err}`))
            .finally(() => {
              // Update our local state from the Vuex store
              this.uniqueAccountName = this.uniqueLabelName;
              this.checkingName = false;
            });
        }
      } catch (error) {
        console.error(`Error checking unique ${this.isArtist ? 'artist' : 'label'} name:`, error);
      } finally {
        this.checkingName = false;
      }
    },
    async createNewAccount() {
      // Touch the validation to trigger validation errors
      this.$v.accountName.$touch();
      
      if (!this.accountName || this.$v.accountName.$error || !this.uniqueAccountName) {
        console.log('Cannot create account:', {
          accountName: this.accountName,
          validationError: this.$v.accountName.$error,
          uniqueName: this.uniqueAccountName
        });
        return;
      }
      
      this.isLoading = true;
      this.createAccountError = false;
      
      const limitImageSize = 10 * 1024 * 1024; // 10MB
      let formData = new FormData();
      
      // Append the name/title based on account type
      if (this.isArtist) {
        formData.append('name', this.accountName);
      } else {
        formData.append('title', this.accountName);
      }
      
      // Handle file upload if a file is selected
      if (this.selectedFile && 
          (this.selectedFile.type === "image/jpeg" || this.selectedFile.type === "image/png") && 
          this.selectedFile.size < limitImageSize) {
        
        // Create a new File object with explicit type information
        const fileType = this.selectedFile.type;
        const fileExtension = fileType === 'image/jpeg' ? 'jpg' : 'png';
        
        // Add file type information to formData
        formData.append('fileType', fileType);
        formData.append('fileExtension', fileExtension);
        
        if (this.isArtist) {
          formData.append('avatar', this.selectedFile);
          formData.append('mediaType', 'avatar');
          formData.append('type', 'artist'); // Add entity type - required by MediaObject entity
        } else {
          formData.append('logo', this.selectedFile);
          formData.append('mediaType', 'logo');
          formData.append('type', 'record_label'); // Add entity type - required by MediaObject entity
        }
      }
      
      // Create artist account
      if (this.isArtist) {
        try {
          // Step 1: Create artist without avatar first
          let artistFormData = new FormData();
          artistFormData.append('name', this.accountName);
          
          await this.CREATE_ARTIST(artistFormData)
            .then(async (response) => {
              // Store the created artist name for later use
              localStorage.setItem('createdArtistName', this.accountName);
              
              // Always move to payment step after account creation
              this.createdAccountName = this.accountName;
              
              // Set client secret if available
              if (this.subscriptionClientSecret) {
                this.clientSecret = this.subscriptionClientSecret;
                this.stripeOptions.clientSecret = this.subscriptionClientSecret;
              }
              
              // Step 2: If we have a photo, upload it as a separate step
              if (this.selectedFile) {
                // Get the created artist ID from the response
                const artistId = response && response.data ? response.data.id : null;
                
                if (artistId) {
                  let mediaFormData = new FormData();
                  mediaFormData.append('file', this.selectedFile);
                  mediaFormData.append('type', 'artist');
                  mediaFormData.append('entity_id', artistId);
                  
                  // Upload the avatar to media_objects endpoint
                  await this.ADD_FILE(mediaFormData)
                    .then(fileResponse => {
                      // Update the artist with the avatar reference
                      if (this.FILE_URL) {
                        return this.CHANGE_ARTIST_DATA({
                          id: artistId,
                          avatar: this.FILE_URL
                        });
                      }
                    })
                    .catch(err => console.log(`Error uploading avatar: ${err}`));
                }
              } else {
                // If client secret is not available, we'll need to create one
                // This will be handled by the payment processor
                console.log('No subscription client secret available, will create one');
              }
              
              // Show payment step
              this.showPaymentStep = true;
              
              // Set up Centrifuge for real-time updates
              this.getCentrifuges('create_artist_', (self) => {
                // When we receive the update, refresh the artists list
                this.GET_YOUR_ARTISTS({ mine: true });
              });
              
              // Refresh the artists list initially
              return this.GET_YOUR_ARTISTS({ mine: true });
            })
            .catch(err => {
              console.error(`Create artist account error: ${err}`);
              this.createAccountError = true;
            })
            .finally(() => {
              setTimeout(() => {
                this.isLoading = false;
              }, 120);
            });
        } catch (error) {
          console.error('Error creating artist:', error);
          this.createAccountError = true;
          this.isLoading = false;
        }
      } else {
        // Create label account
        try {
          await this.CREATE_RECORD_LABEL(formData)
            .then(() => {
              // Store the created label title for later use
              localStorage.setItem('createdLabelTitle', this.accountName);
              
              // Always move to payment step after account creation
              this.createdAccountName = this.accountName;
              
              // Set client secret if available
              if (this.recordLabelClientSecret) {
                this.clientSecret = this.recordLabelClientSecret;
                this.stripeOptions.clientSecret = this.recordLabelClientSecret;
              } else {
                // If client secret is not available, we'll need to create one
                // This will be handled by the payment processor
                console.log('No record label client secret available, will create one');
              }
              
              // Show payment step
              this.showPaymentStep = true;
              
              // Set up Centrifuge for real-time updates
              this.getCentrifuges('create_record_label_', (self) => {
                // When we receive the update, refresh the labels list
                this.GET_RECORD_LABELS();
              });
              
              // Refresh the labels list initially
              return this.GET_RECORD_LABELS();
            })
            .catch(err => {
              console.error(`Create label account error: ${err}`);
              this.createAccountError = true;
            })
            .finally(() => {
              setTimeout(() => {
                this.isLoading = false;
              }, 120);
            });
        } catch (error) {
          console.error('Error creating label:', error);
          this.createAccountError = true;
          this.isLoading = false;
        }
      }
    },
    // Payment related methods
    handleElementReady() {
      console.log('Stripe elements are ready');
    },
    submitPayment() {
      // Check if payment form is ready
      if (!this.$refs.paymentRef) {
        console.error('Payment form not ready');
        return;
      }
      
      // Prevent multiple submissions
      if (this.submitStripeLoader) {
        return;
      }
      
      // Check if we have a client secret
      if (!this.clientSecret) {
        console.error('No client secret available');
        return;
      }
      
      // Show loading state
      this.submitStripeLoader = true;
      
      // Submit the payment form
      this.$refs.paymentRef.submit();
      
      // Add event listeners for Stripe iframe closure
      window.addEventListener(
        "message",
        this.handleStripeMessage,
        false
      );
    },
    
    // Handle messages from Stripe iframe
    handleStripeMessage(event) {
      // Only process messages from Stripe
      if (event.origin !== 'https://js.stripe.com') {
        return;
      }
      
      // Check if the payment form was closed without completion
      if (event.data && event.data.type === 'stripe-frame-closed') {
        this.submitStripeLoader = false;
        window.removeEventListener("message", this.handleStripeMessage);
      }
    },
    
    /**
     * Set up Centrifuge real-time updates with limited reconnection attempts
     * @param {string} channel - The channel to subscribe to
     * @param {Function} callback - Callback function to execute when receiving updates
     */
    getCentrifuges(channel, callback) {
      this.GET_CENTRIFUGE_CLIENT()
        .then(() => {
          const self = this;
          
          // Configure connection with retry limits
          if (this.centrifugeClient && this.centrifugeClient.setConfig) {
            this.centrifugeClient.setConfig({
              maxRetry: 3,              // Max number of reconnect attempts
              minReconnectDelay: 1000,  // Min delay between reconnect attempts (1 second)
              maxReconnectDelay: 5000,  // Max delay between reconnect attempts (5 seconds)
              timeout: 5000             // Connection timeout (5 seconds)
            });
          }
          
          // Create subscription
          const sub = this.centrifugeClient.newSubscription(`${channel}${this.userID}`);
          
          // Store subscription reference for cleanup
          this.activeSubscriptions.push(sub);
          
          // Add event handlers
          sub.on('publication', function (ctx) {
            if (ctx.data.status === 'success') {
              callback(self);
            }
            self.submitStripeLoader = false;
          });
          
          // Handle subscription errors
          sub.on('error', function(err) {
            console.error('Subscription error:', err);
          });
          
          // Subscribe to the channel
          sub.subscribe();
          
          // Connect with disconnect handler
          this.centrifugeClient.on('disconnect', (ctx) => {
            console.log('Centrifuge disconnected after', ctx.count, 'reconnect attempts');
            // After max retries, stop trying to reconnect
            if (ctx.count >= 3) {
              console.log('Max reconnection attempts reached, stopping reconnection');
              this.centrifugeClient.disconnect();
            }
          });
          
          // Connect to the Centrifuge server
          this.centrifugeClient.connect();
        })
        .catch(err => {
          console.error('CentrifugeError', err);
        })
        .finally(() => {
          this.submitStripeLoader = false;
        });
    },
    
    submitPaymentCallback(event) {
      this.submitStripeLoader = false;
      
      // Remove the event listener to prevent memory leaks
      window.removeEventListener("message", this.handleStripeMessage);
      
      if (event.error) {
        console.error('Payment error:', event.error);
        this.paymentError = event.error.message;
        return;
      }
      
      // Only proceed to success if payment was actually completed
      if (event.paymentIntent && event.paymentIntent.status === 'succeeded') {
        console.log('Payment successful:', event);
        this.paymentComplete = true;
        this.showPaymentStep = false;
        this.showSuccessMessage = true;
      }
    },
    backToAccountCreation() {
      // Clean up connections before going back
      this.cleanupConnections();
      this.showPaymentStep = false;
    },
    cancel() {
      // Clean up connections before closing the dialog
      this.cleanupConnections();
      this.$emit('input', false);
      this.$emit('cancel');
    },
    done() {
      // Clean up connections before completing the flow
      this.cleanupConnections();
      
      // Close the dialog
      this.$emit('input', false);
      
      // Emit the event that the parent component is expecting
      if (this.accountType === 'Artist') {
        // For artists, emit the event that matches onArtistCreated in the parent
        this.$emit('created', this.createdAccountName);
      } else {
        // For labels, do the same but specify it's a label
        this.$emit('created', this.createdAccountName);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/mixins/dialog-mixins";

.account-creation-dialog {
  // Use the dialog mixin with appropriate options
  &::v-deep .create_new_account_modal {
    background: #282C47;
    border-radius: 25px;
    overflow: hidden;
    padding: 0;
    position: relative;
    
    .heading {
      padding: 20px 30px;
      
      .heading_new_account {
        display: flex;
        justify-content: center;
        color: #fff;
        font-weight: 500;
        font-size: 24px;
        padding-bottom: 20px;
        line-height: 28px;
        border-bottom: 1px solid rgba(#5F6986, .3);
        margin: 0;
      }
    }
    
    .wrapper_content {
      padding: 20px 30px;
      border-bottom: 1px solid rgba(#5F6986, .3);
      
      &.h165 {
        min-height: 165px;
      }
      
      &.v_center {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .create_account_box {
        width: 100%;
        padding: 0;
        
        .set_name_account {
          margin-bottom: 20px;
          border-bottom: 1px solid rgba(#5F6986, .3);
          padding-bottom: 15px;
          
          .label_name {
            font-size: 16px;
            color: #dcdcdc;
            line-height: 19px;
            margin-bottom: 10px;
          }
          
          > input {
            width: 100%;
            height: 52px;
            background: #0C1324;
            border: 1px solid #282C47;
            border-radius: 5px;
            overflow: hidden;
            color: #fff;
            font-size: 16px;
            padding: 0 16px;
            
            &.error_name {
              border: 1px solid #E94E4E;
            }
            
            &::placeholder {
              color: #7481A6;
            }
            
            &:focus {
              border-color: #20C4F5;
              outline: none;
            }
          }
          
          .err_artist_exists {
            display: flex;
            color: #E94E4E;
            font-size: 12px;
            line-height: 20px;
            padding-left: 5px;
          }
        }
        
        .set_photo {
          margin-top: 20px;
          
          .label_photo {
            font-size: 14px;
            color: #fff;
            margin-bottom: 10px;
          }
          
          .image-wrapper {
            display: flex;
            align-items: flex-start;
            
            /* Cover-style image selector */
            .select_cover_album {
              width: 100px;
              height: 100px;
              border-radius: 5px;
              background-color: #182034;
              overflow: hidden;
              cursor: pointer;
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 15px;
              border: 1px solid rgba(95, 105, 134, 0.3);
              
              &.default_img {
                &:before {
                  display: none;
                }
              }
              
              .cover_select_img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                z-index: 2;
              }
              
              .select_img {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: #20C4F5;
                font-size: 14px;
                z-index: 1;
              }
            }
            
            /* Description text styling */
            .description_ {
              font-size: 12px;
              color: rgba(255, 255, 255, 0.7);
              line-height: 1.5;
              
              .error-msg {
                color: #ff4747;
                display: block;
                margin-top: 5px;
              }
            }
          }
        }
      }
    }
    
    .wrapper_button {
      padding: 20px 30px;
      display: flex;
      justify-content: center;
      gap: 15px;
      
      button {
        min-width: 120px;
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }
}



// Payment form styles
.payment-form-wrapper {
  min-height: 450px;
  height: auto;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0;
}

.info_card_box {
  width: 100%;
  
  .account_data {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding: 0 20px 20px;
    border-bottom: 1px solid rgba(#5F6986, .3);
    
    .description_info {
      display: flex;
      align-items: center;
      
      .image {
        width: 45px;
        height: 45px;
        border-radius: 50%;
        overflow: hidden;
        margin-right: 10px;
        position: relative;
        background-color: #0C1324;
        display: flex;
        align-items: center;
        justify-content: center;
        
        &.default_ava {
          background-color: #182034;
          border-radius: 50%;
        }
      }
      
      .default_user_avatar {
        width: 100%;
        height: 100%;
        background-color: #5F6986;
        background-image: url('/assets/images/svg/square_avatar.svg');
        background-size: contain;
        background-position: center;
        opacity: 0.5;
      }
      
      .name_type {
        .name {
          font-size: 14px;
          color: #fff;
          line-height: 1;
          margin-bottom: 2px;
        }
        
        .type {
          font-size: 12px;
          color: #dcdcdc;
        }
      }
    }
    
    .price_info {
      font-size: 18px;
      font-weight: 500;
      color: #20C4F5;
    }
  }
  
  .card_data {
    width: 100%;
    padding: 0 20px;
    display: block;
    align-items: initial;
  }
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #dcdcdc;
}

// Success message styles
.success-message {
  text-align: center;
  padding: 10px 0;
  
  h3 {
    color: #fff;
    font-size: 20px;
    margin-bottom: 10px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 10px;
  }
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(32, 196, 245, 0.2);
  margin: 0 auto 20px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    width: 30px;
    height: 15px;
    border-left: 3px solid #20C4F5;
    border-bottom: 3px solid #20C4F5;
    transform: rotate(-45deg);
    top: 20px;
    left: 15px;
  }
}
</style>
